import { useId, type CSSProperties } from 'react';
import './LiksSlider.css';

export type LiksSliderProps = {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Accessible name for the range control. */
  'aria-label': string;
  id?: string;
  disabled?: boolean;
  className?: string;
  /** Shown beside the track. Defaults to `String(value)`. */
  valueLabel?: string;
  showValue?: boolean;
};

export function LiksSlider({
  value,
  onChange,
  min,
  max,
  step = 1,
  'aria-label': ariaLabel,
  id,
  disabled = false,
  className,
  valueLabel,
  showValue = true,
}: LiksSliderProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const range = max - min;
  const progressPercent =
    range <= 0 ? 0 : Math.min(100, Math.max(0, ((value - min) / range) * 100));

  return (
    <div
      className={['liks-slider', disabled && 'liks-slider--disabled', className]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        id={inputId}
        type="range"
        className="liks-slider__input"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        style={
          {
            '--liks-slider-progress': `${progressPercent}%`,
          } as CSSProperties
        }
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {showValue && (
        <span className="liks-slider__value" aria-hidden>
          {valueLabel ?? String(value)}
        </span>
      )}
    </div>
  );
}
